import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useProjectStore } from '../../store/projectStore';
import SafeIcon from '../../common/SafeIcon';
import ProspectDetail from '../../components/Prospects/ProspectDetail';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiFilter, FiStar, FiEye, FiMail, FiMoreVertical, FiDownload } = FiIcons;

const Prospects = () => {
  const { prospects, projects, updateProspectStatus } = useProjectStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProspect, setSelectedProspect] = useState(null);

  const filteredProspects = prospects.filter(prospect => {
    const matchesSearch = 
      (prospect.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) || 
      (prospect.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) || false);
    const matchesStatus = statusFilter === 'all' || prospect.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'reviewing': return 'bg-yellow-100 text-yellow-800';
      case 'preselected': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new': return 'Nuevo';
      case 'reviewing': return 'En Revisión';
      case 'preselected': return 'Preseleccionado';
      case 'rejected': return 'Rechazado';
      default: return 'Desconocido';
    }
  };

  const getProjectTitle = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.title : 'Proyecto no encontrado';
  };

  const renderStars = (score) => {
    return Array.from({ length: 5 }, (_, i) => (
      <SafeIcon 
        key={i} 
        icon={FiStar} 
        className={`w-4 h-4 ${i < score ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
      />
    ));
  };

  const handleStatusChange = (id, status) => {
    updateProspectStatus(id, status);
    setSelectedProspect(prev => {
      if (prev && prev.id === id) {
        return { ...prev, status };
      }
      return prev;
    });
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Prospectos</h1>
            <p className="text-gray-600">Gestiona y evalúa a todos tus candidatos</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>Total: {prospects.length}</span>
            <span>•</span>
            <span>Nuevos: {prospects.filter(p => p.status === 'new').length}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <SafeIcon
              icon={FiSearch}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
            />
            <input
              type="text"
              placeholder="Buscar por nombre o apellido..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">Todos los estados</option>
            <option value="new">Nuevos</option>
            <option value="reviewing">En Revisión</option>
            <option value="preselected">Preseleccionados</option>
            <option value="rejected">Rechazados</option>
          </select>
        </div>
      </motion.div>

      {/* Prospects Table */}
      {filteredProspects.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidato
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Proyecto
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Puntuación IA
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProspects.map((prospect) => (
                  <tr key={prospect.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {prospect.name} {prospect.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{prospect.phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{getProjectTitle(prospect.projectId)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        {renderStars(prospect.aiScore)}
                        <span className="ml-2 text-sm text-gray-600">({prospect.aiScore}/5)</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(prospect.status)}`}>
                        {getStatusText(prospect.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(prospect.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setSelectedProspect(prospect)}
                          className="text-primary hover:text-blue-700 p-1"
                          title="Ver detalles"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </button>
                        <button
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="Enviar email"
                        >
                          <SafeIcon icon={FiMail} className="w-4 h-4" />
                        </button>
                        <div className="relative group">
                          <button className="text-gray-400 hover:text-gray-600 p-1">
                            <SafeIcon icon={FiMoreVertical} className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => handleStatusChange(prospect.id, 'reviewing')}
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                              >
                                Marcar en revisión
                              </button>
                              <button
                                onClick={() => handleStatusChange(prospect.id, 'preselected')}
                                className="block px-4 py-2 text-sm text-green-600 hover:bg-green-50 w-full text-left"
                              >
                                Preseleccionar
                              </button>
                              <button
                                onClick={() => handleStatusChange(prospect.id, 'rejected')}
                                className="block px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                              >
                                Rechazar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiFilter} className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {searchTerm || statusFilter !== 'all'
              ? 'No se encontraron prospectos'
              : 'No hay prospectos aún'}
          </h3>
          <p className="text-gray-600">
            {searchTerm || statusFilter !== 'all'
              ? 'Intenta ajustar tus filtros de búsqueda'
              : 'Los candidatos aparecerán aquí cuando completen las entrevistas'}
          </p>
        </motion.div>
      )}

      {/* Prospect Detail Modal */}
      {selectedProspect && (
        <ProspectDetail
          prospect={selectedProspect}
          onClose={() => setSelectedProspect(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
};

export default Prospects;